import { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/node';
import { redirect, useSubmit } from '@remix-run/react';

import { Layout } from '@shieldpay/bumblebee/layout';
import { Modal } from '@shieldpay/bumblebee/modal';
import { Text } from '@shieldpay/bumblebee/text';

import {
  getUserDetailsOrRedirectToLogout,
  maybeAlreadyLoggedIn,
  signInRedirect,
} from '@shieldpay/authentication-ui/auth.server';
import { Login } from '@shieldpay/authentication-ui/components/login/login.tsx';

const APP_NAME = 'Shieldpay-Admin-Dashboard';

export const loader = async ({ request }: LoaderFunctionArgs) => {
  if (!(await maybeAlreadyLoggedIn(request))) return null;

  try {
    // do a test to see if we are logged in server side, and if so redirect to dashboard
    await getUserDetailsOrRedirectToLogout(request);

    return redirect('/');
  } catch (thrown) {
    // if we are not logged in, this will render the login component
    return null;
  }
};

export const action = async ({ request }: ActionFunctionArgs) =>
  signInRedirect(request);

const LoginRoute = () => {
  const { formatMessage } = useIntl();

  const submit = useSubmit();

  const handleLogin = useCallback(async () => {
    submit(
      {
        loggedIn: true,
      },
      {
        method: 'POST',
      },
    );
  }, [submit]);

  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);

  return (
    <Layout stack="column" spacing="basePos4">
      <Login onAuthenticated={handleLogin} issuer={APP_NAME} />

      <Modal
        dismissable
        open={forgotPasswordModalOpen}
        onClose={() => {
          setForgotPasswordModalOpen(false);
        }}
        closeDialogText={formatMessage({ id: 'shared.close' })}
      >
        <Modal.Title>
          <FormattedMessage id="login.forgotPasswordHeading" />
        </Modal.Title>
        <Text paragraph variant="body150">
          <FormattedMessage id="login.forgotPasswordInstructions" />
        </Text>
        <Modal.Actions>
          <Modal.CloseButton>
            <FormattedMessage id="shared.ok" />
          </Modal.CloseButton>
        </Modal.Actions>
      </Modal>
    </Layout>
  );
};

export default LoginRoute;
